import { Button, Input } from "@heroui/react";
import { useState, type FormEvent } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";

import logo from "@/assets/images/bomiel.png";
import VersionLabel from "@/components/VersionLabel";
import { login, LoginError } from "@/lib/auth";
import { useAuth } from "@/lib/auth-context";
import { showError } from "@/lib/toast";

/**
 * ログイン画面。
 *
 * ngtg_msc の login_view に準拠し、ブランドカラーの背景に白いロゴバッジ・
 * 白塗りの入力欄・白いボタンを載せる。認証成功後は元の画面（無ければホーム）へ。
 */
export default function LoginPage() {
  const { status, refresh } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const from = (location.state as { from?: string } | null)?.from ?? "/";

  // すでに認証済みなら元の画面へ（msc の redirect: 認証済みで /login なら / へ）。
  if (status === "authed") return <Navigate to={from} replace />;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email.trim(), password);
      // Cookie がセットされたので認証状態を取り直す。
      await refresh();
      navigate(from, { replace: true });
    } catch (err) {
      // 失敗はフォーム下ではなく Toast で出す（msc の SnackBar に対応）。
      showError(err instanceof LoginError ? err.message : "ログインに失敗しました。");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-dvh flex-col items-center justify-between bg-brand-500 px-4 py-6">
      <div className="flex flex-1 flex-col items-center justify-center">
        {/* ブランドカラー背景でもロゴが映えるよう白い円形バッジに載せる。 */}
        <div className="rounded-full bg-white p-5">
          <img src={logo} alt="BOMIEL" width={96} height={96} />
        </div>
        <h1 className="mt-5 text-[28px] font-black text-white">BOMIEL</h1>

        <form
          className="mt-9 flex w-[min(50vw,400px)] min-w-[280px] flex-col items-center gap-2.5"
          onSubmit={handleSubmit}
        >
          <Input
            aria-label="メールアドレス"
            placeholder="メールアドレス"
            type="email"
            autoComplete="username"
            maxLength={255}
            value={email}
            onValueChange={setEmail}
            isDisabled={submitting}
            isRequired
            variant="flat"
            radius="none"
            classNames={{
              inputWrapper:
                "rounded-field bg-white data-[hover=true]:bg-white group-data-[focus=true]:bg-white",
              input: "text-sm",
            }}
          />
          <Input
            aria-label="パスワード"
            placeholder="パスワード"
            type="password"
            autoComplete="current-password"
            maxLength={255}
            value={password}
            onValueChange={setPassword}
            isDisabled={submitting}
            isRequired
            variant="flat"
            radius="none"
            classNames={{
              inputWrapper:
                "rounded-field bg-white data-[hover=true]:bg-white group-data-[focus=true]:bg-white",
              input: "text-sm",
            }}
          />
          {/* 幅はフォームの 70%、高さ 50px（msc と同じ比率）。 */}
          <Button
            type="submit"
            isLoading={submitting}
            className="mt-6 h-[50px] w-[70%] rounded-tile bg-white font-bold text-brand-500"
          >
            ログイン
          </Button>
        </form>
      </div>

      <VersionLabel className="text-sm text-white" />
    </div>
  );
}
